import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard, Users, Clock, CalendarDays, FileText,
  Building2, Settings, ChevronLeft, ChevronRight, LogOut,
  ClipboardCheck, Wallet, ChevronDown, Shield, Briefcase, Database, ListTodo, BarChart3, Moon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Menu } from 'lucide-react';

const modules = [
  { label: 'HR Management', icon: Users, id: 'hr', coming: false },
  { label: 'Task Management', icon: ListTodo, id: 'tasks', coming: false },
  { label: 'Lead Management', icon: Briefcase, id: 'leads', coming: false },
  { label: 'Case Management', icon: Shield, id: 'cases', coming: true },
];

const navItems = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', roles: ['super_user', 'manager', 'staff'], module: 'hr' },
  { label: 'Check In/Out', icon: Clock, path: '/attendance/check', roles: ['super_user', 'manager', 'staff'], module: 'hr' },
  { label: 'Attendance', icon: ClipboardCheck, path: '/attendance', roles: ['super_user', 'manager', 'staff'], module: 'hr' },
  { label: 'Leaves', icon: FileText, path: '/leaves', roles: ['super_user', 'manager', 'staff'], module: 'hr' },
  { label: 'After-Hours', icon: Moon, path: '/overtime', roles: ['super_user', 'manager', 'staff'], module: 'hr' },
  { label: 'Employees', icon: Users, path: '/employees', roles: ['super_user', 'manager'], module: 'hr' },
  { label: 'Branches', icon: Building2, path: '/branches', roles: ['super_user'], module: 'hr' },
  { label: 'Holidays', icon: CalendarDays, path: '/holidays', roles: ['super_user', 'manager'], module: 'hr' },
  { label: 'Salary', icon: Wallet, path: '/salary', roles: ['super_user', 'manager'], module: 'hr' },
  { label: 'Manual Entry', icon: Database, path: '/admin/manual-entry', roles: ['super_user'], module: 'hr' },
  { label: 'Settings', icon: Settings, path: '/settings', roles: ['super_user', 'manager'], module: 'hr' },
  { label: 'Tasks', icon: ListTodo, path: '/tasks', roles: ['super_user', 'manager', 'staff'], module: 'tasks' },
  { label: 'Leads', icon: Briefcase, path: '/leads', access: 'leads', module: 'leads' },
  { label: 'Analytics', icon: BarChart3, path: '/leads/analytics', access: 'leads', module: 'leads' },
];

const MODULE_ROUTE_MAP = { '/tasks': 'tasks', '/leads': 'leads' };

function moduleForPath(pathname) {
  for (const [prefix, mod] of Object.entries(MODULE_ROUTE_MAP)) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) return mod;
  }
  return 'hr';
}

function useFilteredNav(activeModule) {
  const { user, canAccessLeads } = useAuth();
  return navItems.filter(item => {
    if (!user) return false;
    if (item.module !== activeModule) return false;
    if (item.access === 'leads') return canAccessLeads;
    return item.roles ? item.roles.includes(user.role) : true;
  });
}

function NavLink({ item, collapsed, onClick }) {
  const location = useLocation();
  const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  const Icon = item.icon;

  return (
    <Link
      to={item.path}
      onClick={onClick}
      data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}-link`}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      <Icon className="h-4.5 w-4.5 shrink-0" size={18} />
      {!collapsed && <span>{item.label}</span>}
    </Link>
  );
}

function ModuleSwitcher({ collapsed, activeModule, setActiveModule, availableModules }) {
  const [open, setOpen] = useState(false);
  const current = modules.find(m => m.id === activeModule) || modules[0];
  const CurrentIcon = current.icon;

  if (collapsed) {
    return (
      <div className="px-3 py-2">
        <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center mx-auto">
          <CurrentIcon size={16} className="text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-2">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-primary/5 border border-primary/10 hover:bg-primary/10 transition-colors text-sm"
        data-testid="module-switcher"
      >
        <div className="flex items-center gap-2">
          <CurrentIcon size={16} className="text-primary" />
          <span className="font-medium">{current.label}</span>
        </div>
        <ChevronDown size={14} className={cn("text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="mt-1.5 space-y-0.5 animate-fade-in">
          {modules.map(m => {
            const accessible = availableModules.includes(m.id);
            const selectable = accessible && !m.coming;
            const isActive = m.id === activeModule;
            return (
              <button
                key={m.id}
                disabled={!selectable}
                onClick={() => {
                  if (!selectable) return;
                  setActiveModule(m.id);
                  setOpen(false);
                }}
                data-testid={`module-option-${m.id}`}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors text-left",
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : selectable
                      ? "text-muted-foreground hover:bg-muted hover:text-foreground"
                      : "text-muted-foreground/50 cursor-not-allowed"
                )}
              >
                <div className="flex items-center gap-2">
                  <m.icon size={14} />
                  <span>{m.label}</span>
                </div>
                {m.coming && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">Soon</Badge>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function Sidebar() {
  const { user, logout, isSuperUser, isManager, canAccessLeads } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [activeModule, setActiveModule] = useState(() => moduleForPath(location.pathname));

  useEffect(() => {
    const mod = moduleForPath(location.pathname);
    setActiveModule(prev => (prev === mod ? prev : mod));
  }, [location.pathname]);

  const availableModules = ['hr', 'tasks', ...(canAccessLeads ? ['leads'] : [])];
  const filteredItems = useFilteredNav(activeModule);

  const roleBadge = isSuperUser ? 'Super Admin' : isManager ? 'Manager' : user?.staff_role || 'Staff';

  return (
    <aside
      className={cn(
        'hidden lg:flex flex-col border-r bg-card h-screen sticky top-0 transition-all duration-200',
        collapsed ? 'w-[68px]' : 'w-[264px]'
      )}
    >
      {/* Logo */}
      <div className="flex items-center gap-2 px-4 py-4 border-b">
        {collapsed ? (
          <img src="/visaxpress-logo.svg" alt="VisaXpress" className="h-9 w-auto object-contain shrink-0" style={{minWidth: '32px'}} />
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <img src="/visaxpress-logo.svg" alt="VisaXpress" className="h-9 w-auto object-contain" />
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0 font-heading tracking-wider">FALCON</Badge>
          </div>
        )}
      </div>

      {/* Module Switcher */}
      <ModuleSwitcher
        collapsed={collapsed}
        activeModule={activeModule}
        setActiveModule={setActiveModule}
        availableModules={availableModules}
      />

      <Separator className="mx-3" />

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
        {filteredItems.map((item) => (
          <NavLink key={item.path} item={item} collapsed={collapsed} />
        ))}
      </nav>

      {/* User section */}
      <div className="border-t px-3 py-3">
        {!collapsed && (
          <div className="mb-2 px-2">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground capitalize">{roleBadge}</p>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={logout}
          className="w-full justify-start gap-2 text-muted-foreground hover:text-destructive"
          data-testid="sidebar-logout-button"
        >
          <LogOut size={16} />
          {!collapsed && 'Log out'}
        </Button>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 h-6 w-6 rounded-full border bg-card shadow-sm flex items-center justify-center hover:bg-muted transition-colors"
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}

export function MobileSidebar() {
  const { user, logout, canAccessLeads } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [activeModule, setActiveModule] = useState(() => moduleForPath(location.pathname));

  useEffect(() => {
    const mod = moduleForPath(location.pathname);
    setActiveModule(prev => (prev === mod ? prev : mod));
  }, [location.pathname]);

  const availableModules = ['hr', 'tasks', ...(canAccessLeads ? ['leads'] : [])];
  const filteredItems = useFilteredNav(activeModule);

  return (
    <div className="lg:hidden">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
            <Menu size={20} />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[264px] p-0">
          <div className="flex items-center gap-2 px-4 py-4 border-b">
            <img src="/visaxpress-logo.svg" alt="VisaXpress" className="h-9 object-contain" />
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-heading tracking-wider">FALCON</Badge>
          </div>
          <ModuleSwitcher
            collapsed={false}
            activeModule={activeModule}
            setActiveModule={setActiveModule}
            availableModules={availableModules}
          />
          <Separator className="mx-3" />
          <nav className="px-3 py-3 space-y-0.5">
            {filteredItems.map((item) => (
              <NavLink key={item.path} item={item} onClick={() => setOpen(false)} />
            ))}
          </nav>
          <Separator />
          <div className="px-3 py-3">
            <p className="text-sm font-medium px-2">{user?.name}</p>
            <Button variant="ghost" size="sm" onClick={logout} className="w-full justify-start gap-2 mt-2 text-muted-foreground">
              <LogOut size={16} /> Log out
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
